import { useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { CONTACT } from "@/data/site";
import { Link, NavLink } from "react-router-dom";
import { EASE } from "./motion";

const LINKS = [["Home", "/"], ["Projects", "/projects"], ["Services", "/services"], ["Site visit", "/site-visit"], ["Contact", "/contact"]];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  return (
    <motion.header
      initial={{ y: -64 }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
      className={`sticky top-0 z-50 backdrop-blur-md bg-[#FBFBF9]/90 border-b transition-[border-color,box-shadow] duration-500 ${scrolled ? "border-stone-300 shadow-[0_8px_30px_-20px_rgba(0,0,0,.35)]" : "border-stone-200/80"}`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="font-serif text-2xl tracking-tight text-stone-900" data-testid="nav-logo">
          Carvings<span className="text-[#B85C38]">.</span>
          <span className="ml-2 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500 hidden sm:inline">Architecture</span>
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map(([l, h]) => (
            <NavLink key={h} to={h} end={h === "/"} className={({ isActive }) => `link-line font-mono text-xs uppercase tracking-[0.2em] hover:text-stone-900 ${isActive ? "text-stone-900" : "text-stone-600"}`} data-testid={`nav-link-${l.toLowerCase().replace(" ", "-")}`}>{l}</NavLink>
          ))}
          <a href={CONTACT.ga6} target="_blank" rel="noopener noreferrer" className="link-line inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.2em] text-[#B85C38] hover:text-stone-900" data-testid="nav-link-ga6">GA6 <ArrowUpRight size={12} /></a>
          <Link to="/contact" className="btn-dark" data-testid="nav-enquire-btn">Enquire</Link>
        </nav>
        <motion.button whileTap={{ scale: 0.9 }} className="md:hidden p-3 -mr-3" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} data-testid="nav-mobile-toggle">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span key={open ? "x" : "m"} className="block" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </motion.span>
          </AnimatePresence>
        </motion.button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            className="md:hidden border-t border-stone-200 bg-[#FBFBF9] px-6 py-8 flex flex-col gap-5 overflow-hidden"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.5, ease: EASE }}
            data-testid="nav-mobile-menu"
          >
            {LINKS.map(([l, h], i) => (
              <motion.div key={h} initial={{ x: -24, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }} whileTap={{ x: 8 }}>
                <Link to={h} onClick={() => setOpen(false)} className="flex items-center gap-4 font-serif text-3xl text-stone-900 py-1" data-testid={`nav-mobile-link-${l.toLowerCase().replace(" ", "-")}`}>
                  <span className="font-mono text-[10px] text-stone-400 tracking-[0.2em]">0{i + 1}</span>{l}
                </Link>
              </motion.div>
            ))}
            <motion.a href={CONTACT.ga6} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} initial={{ x: -24, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.08 + LINKS.length * 0.06, duration: 0.5, ease: EASE }}
              className="mt-2 font-serif text-3xl text-[#B85C38] inline-flex items-center gap-2 py-1" data-testid="nav-mobile-link-ga6">GA6 Microcement <ArrowUpRight size={18} /></motion.a>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
